import { Carousel, CarouselProps } from "antd";
import React, { FC, useMemo, useState } from "react";
import { IncFaIcon, ISaxIcon } from "../../../icons";

interface Props extends CarouselProps {
  expandingDots?: boolean; // New prop to control dot behavior
  bottomRightArrows?: boolean; // New prop to control arrow position and style
}

export const IncCarousel: FC<Props> = props => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const {
    className: pClassName = "",
    children,
    accessibility = true,
    arrows = true,
    centerPadding = "10px",
    dotPosition = "bottom",
    dots: dotsCustomisation,
    infinite = false,
    expandingDots = false, // Default to false (consistent dots)
    bottomRightArrows = false, // Default to false (top right arrows)
    slidesToScroll = 3,
    slidesToShow = 3,
    afterChange,
    ...restProps
  } = props;

  // Calculate total pages
  const totalSlides = React.Children.count(children);
  const totalPages = Math.ceil(totalSlides / slidesToScroll);
  const currentPage = Math.floor(currentSlide / slidesToScroll) + 1;

  // Handle slide change
  const handleAfterChange = (current: number) => {
    setCurrentSlide(current);
    if (afterChange) {
      afterChange(current);
    }
  };

  // Conditional arrow styling based on bottomRightArrows prop
  const nextArrow = bottomRightArrows ? (
    <div>
      <IncFaIcon
        color="#38acff"
        iconName="circle-chevron-right"
        size="xs"
        variant="regular"
      />
    </div>
  ) : (
    <div>
      <ISaxIcon
        iconName="ArrowRight2"
        style={{ color: "white" }}
        variant="Outline"
      />
    </div>
  );

  const prevArrow = bottomRightArrows ? (
    <div>
      <IncFaIcon
        color="#38acff"
        iconName="circle-chevron-left"
        size="xs"
        variant="regular"
      />
    </div>
  ) : (
    <div>
      <ISaxIcon
        iconName="ArrowLeft2"
        style={{ color: "white" }}
        variant="Outline"
      />
    </div>
  );

  const className = `inc-carousel ${expandingDots ? "expanding-dots" : "consistent-dots"} ${bottomRightArrows ? "bottom-right-arrows" : ""} ${pClassName}`;

  const dots = useMemo(() => {
    if (typeof dotsCustomisation === "boolean") {
      return dotsCustomisation;
    } else if (typeof dotsCustomisation === "object") {
      return {
        ...(dotsCustomisation || {}),
        className: `custom-dots ${dotsCustomisation?.className || ""}`
      };
    }

    return {
      className: "custom-dots"
    };
  }, [dotsCustomisation]);

  return (
    <div className={className}>
      <Carousel
        accessibility={accessibility}
        afterChange={handleAfterChange}
        arrows={arrows}
        centerPadding={centerPadding}
        dotPosition={dotPosition}
        dots={dots}
        infinite={infinite}
        nextArrow={nextArrow}
        prevArrow={prevArrow}
        slidesToScroll={slidesToScroll}
        slidesToShow={slidesToShow}
        {...restProps}
      >
        {children}
      </Carousel>

      {/* Page counter displayed when bottomRightArrows is true */}
      {bottomRightArrows && (
        <div className="page-counter">
          {currentPage}/{totalPages}
        </div>
      )}
    </div>
  );
};
